/**
 * Parcel music: plays the stream and turns it into a 0..1 "energy" value each frame
 * for the engine's lights.
 *
 * Why analysis is local: every listener's stream is buffered differently (seconds
 * apart), so beat effects have to follow what *this* listener hears. See CLAUDE.md.
 *
 * Analysis needs CORS: the stream must be same-origin (Caddy proxies /stream) or send
 * Access-Control-Allow-Origin. A media element routed through Web Audio plays a
 * non-CORS stream as *silence*, so for those we switch to a second, plain element:
 * music without beat analysis beats no music.
 *
 * A live stream drops (the DJ's connection hiccups, Icecast restarts, venue Wi-Fi), and a
 * browser may refuse to start audio without a fresh click (Safari, iOS). So the music has a
 * state the UI follows: it offers a play button whenever there's music that isn't playing,
 * and reconnects by itself after a drop.
 */

/**
 * - `off`: the parcel has no music.
 * - `connecting`: starting or buffering.
 * - `playing`: audible (or at volume 0, but running).
 * - `needs-click`: the browser wants a user gesture before it plays; show the play button.
 * - `reconnecting`: the stream dropped; trying again shortly.
 */
export type MusicState = 'off' | 'connecting' | 'playing' | 'needs-click' | 'reconnecting';

/** What Music needs from the browser; tests pass fakes. */
export interface MusicDeps {
  createAudio(): HTMLAudioElement;
  createContext(): AudioContext;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

const browserDeps: MusicDeps = {
  createAudio: () => new Audio(),
  createContext: () => new AudioContext(),
  setTimeout: (fn, ms) => window.setTimeout(fn, ms),
  clearTimeout: (handle) => window.clearTimeout(handle as number),
};

/**
 * How long a stream may wait for data before it counts as dropped. A live stream on venue
 * Wi-Fi can stall with the connection still open: then the element only says `waiting`, and
 * neither `error` nor `ended` ever comes.
 */
export const STALL_TIMEOUT_MS = 10_000;

/** How fast the beat level falls back after a kick, and how long the running average is. */
const ENERGY_RELEASE_S = 0.158;
const ENERGY_AVERAGE_S = 0.825;

/** Reconnect delays after a drop: 1 s, 2 s, 4 s … capped at 30 s, reset once it plays again. */
export function reconnectDelayMs(attempt: number): number {
  return Math.min(30_000, 1000 * 2 ** Math.max(0, attempt));
}

export class Music {
  /** Routed through Web Audio for analysis; always crossOrigin="anonymous". */
  private readonly analysed: HTMLAudioElement;
  /** Never touches Web Audio; used when the stream has no CORS headers. */
  private readonly plain: HTMLAudioElement;
  private active: HTMLAudioElement;

  private context: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  /** Listening volume for the analysed path, applied *after* the analyser. */
  private gain: GainNode | null = null;
  private vol = 1;
  private bins: Uint8Array<ArrayBuffer> = new Uint8Array(0);
  private url: string | null = null;
  private gestureSeen = false;
  private level = 0;
  private average = 0;
  private lastEnergyMs: number | null = null;

  private stateNow: MusicState = 'off';
  private listener: ((state: MusicState) => void) | null = null;
  /** The analysed element has played this URL: a later error is a drop, not missing CORS. */
  private analysedPlayed = false;
  private attempt = 0;
  private retry: unknown = null;
  /** Running while the stream waits for data; fires a reconnect if it never comes. */
  private stallTimer: unknown = null;

  constructor(private readonly deps: MusicDeps = browserDeps) {
    this.analysed = deps.createAudio();
    this.plain = deps.createAudio();
    this.active = this.analysed;
    for (const el of [this.analysed, this.plain]) {
      el.preload = 'none';
      el.addEventListener('playing', () => this.onPlaying(el));
      el.addEventListener('waiting', () => this.onWaiting(el));
      el.addEventListener('ended', () => this.onDropped(el));
      el.addEventListener('error', () => this.onError(el));
      el.addEventListener('pause', () => this.onPaused(el));
    }
    this.analysed.crossOrigin = 'anonymous';
  }

  get state(): MusicState {
    return this.stateNow;
  }

  /** Called on every state change. One listener: the UI. */
  onStateChange(listener: (state: MusicState) => void): void {
    this.listener = listener;
  }

  get currentUrl(): string | null {
    return this.url;
  }

  get playing(): boolean {
    return this.stateNow === 'playing';
  }

  /** False until a user gesture has let us start audio. */
  get unlocked(): boolean {
    return this.gestureSeen;
  }

  /** True when lights follow the music (stream is CORS-readable). */
  get analysing(): boolean {
    return this.active === this.analysed && this.playing;
  }

  get volume(): number {
    return this.vol;
  }

  /**
   * 0..1. On the analysed path this is a gain node *after* the analyser, so turning the
   * music down doesn't weaken the beat the lights follow. The plain (non-CORS) element
   * has no analysis, so its own volume does it.
   */
  set volume(v: number) {
    this.vol = Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 1;
    if (this.gain) this.gain.gain.value = this.vol;
    this.plain.volume = this.vol;
  }

  setUrl(url: string | null): void {
    if (url === this.url) return;
    this.url = url;
    this.stopAll();
    this.attempt = 0;
    this.analysedPlayed = false;
    if (!url) {
      this.setState('off');
      return;
    }
    this.active = this.analysed;
    this.analysed.src = url;
    if (this.gestureSeen) void this.play();
    else this.setState('needs-click');
  }

  /** Call from a click/tap handler: browsers only start audio after a user gesture. */
  async unlock(): Promise<void> {
    this.gestureSeen = true;
    if (!this.context) {
      this.context = this.deps.createContext();
      const source = this.context.createMediaElementSource(this.analysed);
      this.analyser = this.context.createAnalyser();
      this.analyser.fftSize = 1024;
      this.analyser.smoothingTimeConstant = 0.6;
      this.bins = new Uint8Array(this.analyser.frequencyBinCount);
      this.gain = this.context.createGain();
      this.gain.gain.value = this.vol;
      // stream → analyser → volume → speakers: the analyser always sees the full signal.
      source.connect(this.analyser);
      this.analyser.connect(this.gain);
      this.gain.connect(this.context.destination);
    }
    try {
      await this.context.resume();
    } catch {
      // Not allowed yet; play() below reports it as needs-click.
    }
    if (this.url) await this.play();
  }

  /**
   * Bass energy relative to its recent average, 0..1; peaks on kicks. Call every frame,
   * with the frame's time in ms.
   *
   * The decay and the average run on time, not frames: at 60 fps a peak falls to 5% within
   * a beat, but per-frame constants at 8 fps (a phone struggling, or software rendering)
   * would barely let it fall before the next kick, and the lights would stay maxed out.
   */
  energy(nowMs: number = performance.now()): number {
    if (!this.analyser || !this.context || !this.analysing) return 0;
    const dt =
      this.lastEnergyMs === null ? 1 / 60 : Math.min(0.25, Math.max(0, (nowMs - this.lastEnergyMs) / 1000));
    this.lastEnergyMs = nowMs;
    this.analyser.getByteFrequencyData(this.bins);
    // Bins below ~150 Hz carry the kick and bass.
    const binHz = this.context.sampleRate / this.analyser.fftSize;
    const top = Math.max(1, Math.min(this.bins.length, Math.round(150 / binHz)));
    let sum = 0;
    for (let i = 0; i < top; i++) sum += this.bins[i] ?? 0;
    const bass = sum / top / 255;

    // The track's typical level, over ~0.8 s; and the level, which jumps up on a kick and
    // falls back over ~0.16 s. (At 60 fps these are the old 2% and ×0.9 per frame.)
    this.average += (bass - this.average) * (1 - Math.exp(-dt / ENERGY_AVERAGE_S));
    this.level = Math.max(bass, this.level * Math.exp(-dt / ENERGY_RELEASE_S));
    return Math.min(1, Math.max(0, (this.level - this.average * 0.85) * 4));
  }

  private setState(state: MusicState): void {
    if (state === this.stateNow) return;
    this.stateNow = state;
    this.listener?.(state);
  }

  private async play(): Promise<void> {
    this.setState('connecting');
    const el = this.active;
    try {
      await el.play();
    } catch (e) {
      if (el !== this.active || !this.url) return; // superseded meanwhile
      if (e instanceof DOMException && e.name === 'NotAllowedError') {
        // Autoplay policy: only a click can start it now.
        this.setState('needs-click');
      } else if (e instanceof DOMException && e.name === 'AbortError') {
        // A new src or a pause interrupted it; whatever did that decides what's next.
      } else {
        this.onDropped(el);
      }
    }
  }

  private stopAll(): void {
    if (this.retry !== null) this.deps.clearTimeout(this.retry);
    this.retry = null;
    this.clearStall();
    for (const el of [this.analysed, this.plain]) {
      el.pause();
      el.removeAttribute('src');
      el.load();
    }
  }

  private onPlaying(el: HTMLAudioElement): void {
    if (el !== this.active) return;
    if (el === this.analysed) this.analysedPlayed = true;
    this.attempt = 0;
    this.clearStall();
    this.setState('playing');
  }

  private clearStall(): void {
    if (this.stallTimer !== null) this.deps.clearTimeout(this.stallTimer);
    this.stallTimer = null;
  }

  /**
   * Paused by something other than us (an incoming call on a phone, the OS taking the audio
   * device): offer the button. Our own pauses come from stopAll, after which the state is
   * never `playing` by the time the event arrives.
   */
  private onPaused(el: HTMLAudioElement): void {
    if (el === this.active && this.url && this.stateNow === 'playing') this.setState('needs-click');
  }

  private onWaiting(el: HTMLAudioElement): void {
    if (el !== this.active || !this.url) return;
    if (this.stateNow === 'playing') this.setState('connecting');
    if (this.stallTimer === null) {
      this.stallTimer = this.deps.setTimeout(() => {
        this.stallTimer = null;
        if (el === this.active && this.stateNow === 'connecting') this.onDropped(el);
      }, STALL_TIMEOUT_MS);
    }
  }

  private onError(el: HTMLAudioElement): void {
    // `error` is reset when a new src starts loading, so this also skips stale events.
    if (!this.url || el !== this.active || el.error === null) return;
    if (el === this.analysed && !this.analysedPlayed) {
      // Never played through Web Audio: most likely no CORS headers. Keep the music, lose
      // the beat analysis.
      console.warn('Music stream not CORS-readable; playing without beat analysis:', this.url);
      this.active = this.plain;
      this.plain.src = this.url;
      if (this.gestureSeen) void this.play();
      else this.setState('needs-click');
      return;
    }
    this.onDropped(el);
  }

  /** The stream ended or broke: try the same URL again, backing off. */
  private onDropped(el: HTMLAudioElement): void {
    if (!this.url || el !== this.active || this.retry !== null) return;
    this.clearStall();
    this.setState('reconnecting');
    const url = this.url;
    this.retry = this.deps.setTimeout(() => {
      this.retry = null;
      if (this.url !== url) return;
      // A fresh load: a live stream can't resume where it left off.
      el.src = url;
      void this.play();
    }, reconnectDelayMs(this.attempt++));
  }
}

/** Where the listening volume is remembered between visits. */
const VOLUME_KEY = 'opensim-web.volume';

/** The saved volume, or full volume if none (or something unreadable) is stored. */
export function loadVolume(storage: Pick<Storage, 'getItem'>): number {
  const saved = Number.parseFloat(storage.getItem(VOLUME_KEY) ?? '');
  return Number.isFinite(saved) ? Math.min(1, Math.max(0, saved)) : 1;
}

export function saveVolume(storage: Pick<Storage, 'setItem'>, volume: number): void {
  try {
    storage.setItem(VOLUME_KEY, String(volume));
  } catch {
    // Storage full or disabled (private mode): the setting just won't persist.
  }
}

/** A synthetic 124 BPM pulse so the dance floor moves in demo mode without a stream. */
export function demoBeat(timeMs: number, bpm = 124): number {
  const beat = (timeMs / 60000) * bpm;
  const phase = beat - Math.floor(beat);
  return Math.pow(1 - phase, 4);
}
